/**
 * This is a forked subset of CreateReportModal, currently only used
 * to export Panel2 panels from the artifacts page to reports.
 *
 * It is simpler and doesn't use the first modal to pick the report
 * template type. I also cleaned up some of the UI behavior (loading
 * & disabled states), and simplified and reorganized a bunch of the
 * code for readability.
 */
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {clsx} from 'clsx';
import * as _ from 'lodash';
import React, {type FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router';
import {useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from 'semantic-ui-react';

import {
  type ProjectReportsQuery,
  useEntityProjectsReportsQuery,
} from '../generated/graphql';
import {useApolloClient} from '../state/hooks';
import {pushReport} from '../state/reports/hooks';
import {appendSectionsToReport} from '../state/reports/reportSections';
import {useViewer} from '../state/viewer/hooks';
import {type ReportConfig} from '../state/views/report/types';
import {getEmptyReportConfig} from '../util/report';
import DraftWarningModal from './ReportModals/DraftWarningModal';
import {type WBSlateElementType} from './Slate/WBSlate/types';
import * as S from './WBModal.styles';

const NEW_REPORT_ID = 'new';

interface AddToReportProps {
  trigger: JSX.Element;
  reportBlock: WBSlateElementType;
}

interface DropdownOption {
  text: string;
  value: string | number;
  label?: React.JSX.Element;
}

const DRAFT_APPEND_MAIN_TEXT =
  'You have unsaved edits to this report. Do you want to resume or discard these unsaved edits?';
const DRAFT_APPEND_DISCARD_TEXT = 'Discard';

type EntityProjectsReportsQuery = ReturnType<
  typeof useEntityProjectsReportsQuery
>;

export function getProjectsFromEntityProjectsReportsQuery(
  query: EntityProjectsReportsQuery
) {
  return _.compact(query.data?.entity?.projects?.edges.map(e => e.node) ?? []);
}

interface AddToReportOptionsByEntityParams {
  entityProjectsReportsQuery: EntityProjectsReportsQuery;
  currentReport?: {
    id: string;
    parentID?: string;
  };
  currentProjectReportsQuery?: ProjectReportsQuery | undefined;
}

export function useAddToReportOptionsByEntity({
  entityProjectsReportsQuery,
  currentReport,
  currentProjectReportsQuery,
}: AddToReportOptionsByEntityParams) {
  const projectOptions: DropdownOption[] = useMemo(() => {
    if (entityProjectsReportsQuery.data == null) {
      return [];
    }
    const opts: DropdownOption[] = [];
    // if the current project is in the list of options, filter it out
    // and use the sent in one instead since it's not guaranteed to be in the
    // first 100 fetched projects
    const projectsWithoutCurrentProject =
      entityProjectsReportsQuery.data?.entity?.projects?.edges.filter(
        e => e.node?.id !== currentProjectReportsQuery?.project?.id
      );
    if (currentProjectReportsQuery?.project) {
      opts.push({
        text: currentProjectReportsQuery.project.name,
        value: currentProjectReportsQuery.project.id,
      });
    }
    opts.push(
      ...(projectsWithoutCurrentProject?.map(e => ({
        text: e.node?.name ?? '',
        value: e.node?.id ?? '',
      })) ?? [])
    );
    return opts;
  }, [entityProjectsReportsQuery, currentProjectReportsQuery]);

  const reportOptionsByProjectID: {
    [id: string]: DropdownOption[];
  } = useMemo(() => {
    if (entityProjectsReportsQuery.data == null) {
      return {};
    }
    const optionsByID: {
      [id: string]: DropdownOption[];
    } = {};
    const projects = getProjectsFromEntityProjectsReportsQuery(
      entityProjectsReportsQuery
    );
    if (
      currentProjectReportsQuery?.project?.id &&
      !projects.find(p => p.id === currentProjectReportsQuery?.project?.id)
    ) {
      const reports =
        currentProjectReportsQuery?.project?.reports?.edges?.map(r => r.node) ??
        [];
      optionsByID[currentProjectReportsQuery?.project?.id] = [
        {text: 'Create a new report', value: NEW_REPORT_ID},
        ...reports
          .map(r => ({
            text: r?.displayName ?? '',
            value: r?.id ?? '',
          }))
          .filter(o => {
            if (currentReport == null) {
              return true;
            }
            return o.value !== (currentReport.parentID ?? currentReport.id);
          }),
      ];
    }
    for (const p of projects) {
      const reports = p.reports?.edges?.map(r => r.node) ?? [];
      optionsByID[p.id] = [
        {text: 'Create a new report', value: NEW_REPORT_ID},
        ...reports
          .map(r => ({
            text: r?.displayName ?? '',
            value: r?.id ?? '',
          }))
          .filter(o => {
            if (currentReport == null) {
              return true;
            }
            return o.value !== (currentReport.parentID ?? currentReport.id);
          }),
      ];
    }
    return optionsByID;
  }, [entityProjectsReportsQuery, currentReport, currentProjectReportsQuery]);

  const projectInfoByID: {
    [id: string]: {
      entityName: string;
      name: string;
    };
  } = useMemo(() => {
    if (entityProjectsReportsQuery.data == null) {
      return {};
    }
    const infoByID: {
      [id: string]: {
        entityName: string;
        name: string;
      };
    } = {};
    const projects = getProjectsFromEntityProjectsReportsQuery(
      entityProjectsReportsQuery
    );
    if (
      currentProjectReportsQuery?.project?.id &&
      !projects.find(p => p.id === currentProjectReportsQuery?.project?.id)
    ) {
      infoByID[currentProjectReportsQuery.project.id] = {
        entityName: currentProjectReportsQuery?.project?.entityName,
        name: currentProjectReportsQuery?.project?.name,
      };
    }
    for (const p of projects) {
      infoByID[p.id] = {
        entityName: p.entityName,
        name: p.name,
      };
    }
    return infoByID;
  }, [entityProjectsReportsQuery, currentProjectReportsQuery]);

  return {
    projectOptions,
    reportOptionsByProjectID,
    projectInfoByID,
  };
}

const useReportActions = (onActionDone: () => void) => {
  const client = useApolloClient();
  const history = useHistory();
  const [saving, setSaving] = useState(false);

  const createReport = useCallback(
    (entityName: string, projectName: string, config: ReportConfig) => {
      setSaving(true);
      const doCreate = async () => {
        await pushReport({
          client,
          entityName,
          projectName,
          config,
          reportName: 'New report',
          opts: {
            newTab: true,
          },
          history,
        });
        onActionDone();
      };
      doCreate();
    },
    [client, onActionDone, history]
  );

  const addBlockToReport = useCallback(
    (
      entityName: string,
      projectName: string,
      reportID: string,
      block: WBSlateElementType
    ) => {
      setSaving(true);
      const doAppend = async () => {
        await appendSectionsToReport({
          client,
          entityName,
          projectName,
          reportID,
          newBlocks: [block],
          opts: {newTab: true},
          history,
        });
        onActionDone();
      };
      doAppend();
    },
    [client, onActionDone, history]
  );

  return {saving, createReport, addBlockToReport};
};

export const AddToReportModalContent: FC<
  AddToReportProps & {
    closeModal(): void;
  }
> = ({reportBlock, closeModal}) => {
  const viewer = useViewer();

  const [draftWarningModalOpen, setDraftWarningModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedProjectID, setSelectedProjectID] = useState('');

  const onChangeEntity = useCallback((e, data) => {
    setSelectedEntity(data.value);
    setSelectedProjectID('');
    setSelectedReportID(NEW_REPORT_ID);
    setProjectSearchQuery('');
  }, []);

  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const onProjectSearchQueryChange = useCallback(
    (e, data) => setProjectSearchQuery(data.searchQuery),
    [setProjectSearchQuery]
  );
  const onChangeProject = useCallback(
    (e, data) => {
      setSelectedProjectID(data.value);
      setSelectedReportID(NEW_REPORT_ID);
    },
    [setSelectedProjectID]
  );

  const [reportSearchQuery, setReportSearchQuery] = useState('');
  const onReportSearchQueryChange = useCallback(
    (e, data) => setReportSearchQuery(data.searchQuery),
    [setReportSearchQuery]
  );

  const [selectedReportID, setSelectedReportID] = useState(NEW_REPORT_ID);
  const onChangeReport = useCallback(
    (e, data) => {
      setSelectedReportID(data.value);
      setReportSearchQuery('');
    },
    [setSelectedReportID, setReportSearchQuery]
  );

  const entityOptions = useMemo(() => {
    return (
      viewer?.teams?.edges.map(e => ({
        text: e.node?.name ?? '',
        value: e.node?.name ?? '',
      })) ?? []
    );
  }, [viewer]);

  // If the user hasn't chosen a project ID, default to the one from the current
  // route. If the current route doesn't contain entityName and projectName
  // default to the first project we find.
  const {entityName: defaultEntityName, projectName: defaultProjectName} =
    useParams<{entityName?: string; projectName?: string}>();

  const targetEntityName = useMemo(() => {
    if (selectedEntity !== '') {
      return selectedEntity;
    }
    const defaultEntity = entityOptions.find(
      e => e.value === defaultEntityName
    );
    return defaultEntity?.value ?? '';
  }, [selectedEntity, defaultEntityName, entityOptions]);

  const entityProjectsReportsQuery = useEntityProjectsReportsQuery({
    variables: {
      entityName: targetEntityName,
      first: 100,
      pattern: projectSearchQuery,
    },
    fetchPolicy: 'cache-and-network',
    skip: targetEntityName === '',
  });
  const projects = useMemo(
    () => getProjectsFromEntityProjectsReportsQuery(entityProjectsReportsQuery),
    [entityProjectsReportsQuery]
  );

  const {projectOptions, reportOptionsByProjectID, projectInfoByID} =
    useAddToReportOptionsByEntity({
      entityProjectsReportsQuery,
    });

  useEffect(() => {
    // exit early if the selected project is already set or the query is still loading
    if (selectedProjectID !== '' || entityProjectsReportsQuery.loading) {
      return;
    }

    const defaultProject = projects.find(
      p => p.entityName === defaultEntityName && p.name === defaultProjectName
    );
    if (defaultProject != null) {
      setSelectedProjectID(defaultProject.id);
      return;
    }
    // didn't find the default project, use first project instead
    const firstProject = projects[0];
    if (firstProject != null) {
      setSelectedProjectID(firstProject.id);
    }
  }, [
    setSelectedProjectID,
    selectedEntity,
    selectedProjectID,
    defaultEntityName,
    defaultProjectName,
    projects,
    entityProjectsReportsQuery.loading,
  ]);

  const targetProjectName = useMemo(() => {
    const projectInfo =
      selectedProjectID != null
        ? projectInfoByID[selectedProjectID]
        : undefined;
    return projectInfo?.name ?? '';
  }, [projectInfoByID, selectedProjectID]);

  const reportConfig = useMemo(() => {
    const conf = getEmptyReportConfig([reportBlock]);
    conf.width = 'fluid';
    return conf;
  }, [reportBlock]);

  const {saving, createReport, addBlockToReport} = useReportActions(closeModal);
  const {loading} = entityProjectsReportsQuery;
  const busy = loading || saving;
  return (
    <>
      <S.WBModalHeader>Add to report</S.WBModalHeader>
      <S.WBModalContent>
        <div className={clsx('destination-section')}>
          <p>Pick a destination report.</p>
          <div className="dest-row">
            <div className="dest-row-dest">Entity</div>
            <ModifiedDropdown
              selection
              search
              className="save-snapshot-modal__select"
              options={entityOptions}
              value={targetEntityName}
              onChange={onChangeEntity}
            />
          </div>
          <div className="dest-row">
            <div className="dest-row-dest">Project</div>
            <ModifiedDropdown
              selection
              loading={loading}
              disabled={busy}
              className="save-snapshot-modal__select"
              options={projectOptions}
              value={selectedProjectID}
              onChange={onChangeProject}
              search
              searchQuery={projectSearchQuery}
              onSearchChange={onProjectSearchQueryChange}
            />
          </div>
          <div className="dest-row">
            <div className="dest-row-dest">Report</div>
            <ModifiedDropdown
              selection
              loading={loading}
              disabled={busy}
              className="save-snapshot-modal__select"
              options={reportOptionsByProjectID[selectedProjectID] ?? []}
              value={selectedReportID}
              onChange={onChangeReport}
              search
              searchQuery={reportSearchQuery}
              onSearchChange={onReportSearchQueryChange}
            />
          </div>
        </div>
      </S.WBModalContent>
      <S.WBModalActions>
        <Button
          primary
          loading={saving}
          disabled={busy}
          onClick={() => {
            if (selectedReportID === NEW_REPORT_ID) {
              createReport(targetEntityName, targetProjectName, reportConfig);
            } else {
              setDraftWarningModalOpen(true);
            }
          }}
          content={
            selectedReportID === NEW_REPORT_ID
              ? 'Create report'
              : 'Add To Report'
          }
        />
      </S.WBModalActions>

      {draftWarningModalOpen && selectedReportID !== NEW_REPORT_ID && (
        <DraftWarningModal
          parentViewID={selectedReportID}
          mainText={DRAFT_APPEND_MAIN_TEXT}
          discardText={DRAFT_APPEND_DISCARD_TEXT}
          renderResumeButton={draftID => (
            <Button
              size="tiny"
              loading={saving}
              disabled={busy}
              onClick={() => {
                const projectInfo = projectInfoByID[selectedProjectID];
                if (projectInfo == null) {
                  return;
                }
                const {entityName, name} = projectInfo;
                addBlockToReport(entityName, name, draftID, reportBlock);
              }}>
              Resume
            </Button>
          )}
          flipButtonColors
          onNoDraftFound={() => {
            addBlockToReport(
              targetEntityName,
              targetProjectName,
              selectedReportID,
              reportBlock
            );
          }}
          onDiscard={() => {
            addBlockToReport(
              targetEntityName,
              targetProjectName,
              selectedReportID,
              reportBlock
            );
          }}
          onClose={() => setDraftWarningModalOpen(false)}
        />
      )}
    </>
  );
};

export const AddToReportModal: FC<AddToReportProps> = props => {
  const [open, setOpen] = useState(false);
  return (
    <S.WBModal
      className="save-snapshot-modal"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={props.trigger}>
      <AddToReportModalContent {...props} closeModal={() => setOpen(false)} />
    </S.WBModal>
  );
};
